import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { LOCATION_TYPE_LABEL_HU, LocationType, parseBarcode } from '@persped/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateLocation } from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';

/**
 * Új lokáció (US-LOCATION-1).
 *
 * A vonalkód-előtag és a típus összefüggését KLIENSOLDALON is ellenőrizzük
 * (`parseBarcode`, ugyanaz a függvény, amit a Zebra app használ), hogy a
 * felhasználó azonnal lássa a hibát. A szerver ugyanezt újraellenőrzi
 * (`BARCODE_TYPE_MISMATCH`) — a kliens validáció kényelem, nem védelem.
 */
export function NewLocationDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const create = useCreateLocation();
  const [barcode, setBarcode] = useState('');
  const [name, setName] = useState('');
  const [locationType, setLocationType] = useState<string>(LocationType.STORAGE);
  const [levelCount, setLevelCount] = useState('4');
  const [notes, setNotes] = useState('');

  const isPallet = locationType === LocationType.PALLET;
  const isShelf = locationType === LocationType.SHELF;
  const parsed = parseBarcode(barcode);
  // A vonalkód-előtag és a típus egyezzen — a szerver is ellenőrzi, de itt
  // azonnal, gépelés közben látszik.
  const vartKind = isPallet ? 'PALLET' : isShelf ? 'SHELF' : 'LOCATION';
  const barcodeOk = parsed.ok && parsed.kind === vartKind;
  const prefixHint = isPallet ? 'RKL-###' : isShelf ? 'POL-###' : 'LOC-###';
  const valid = barcodeOk && name.trim().length > 0;

  const close = () => {
    create.reset();
    setBarcode('');
    setName('');
    setLocationType(LocationType.STORAGE);
    setLevelCount('4');
    setNotes('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Új lokáció</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Típus"
            value={locationType}
            onChange={(e) => setLocationType(e.target.value)}
            helperText="A típus később NEM módosítható."
            fullWidth
          >
            {Object.values(LocationType).map((t) => (
              <MenuItem key={t} value={t}>
                {LOCATION_TYPE_LABEL_HU[t]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Vonalkód"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value.toUpperCase())}
            error={barcode.length > 0 && !barcodeOk}
            helperText={`Várt formátum: ${prefixHint}`}
            slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
            fullWidth
          />

          <TextField
            label="Név"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />

          {isPallet && (
            <Alert severity="info">
              A raklap egyetlen, <strong>1-es szinttel</strong> jön létre. További szint akkor
              nyitható, amikor az alatta lévőn már van mappa — ahogy az elválasztó lemez is csak
              teli szintre kerül.
            </Alert>
          )}

          {isShelf && (
            <>
              <TextField
                select
                label="Szintek száma"
                value={levelCount}
                onChange={(e) => setLevelCount(e.target.value)}
                fullWidth
                required
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20].map((n) => (
                  <MenuItem key={n} value={String(n)}>
                    {n}
                  </MenuItem>
                ))}
              </TextField>
              <Alert severity="info">
                A polc szintjeit a <strong>szerkezete</strong> adja: mind azonnal létrejön, és
                üresen is megmarad. Egy polcszintre mappa és raklap is betárolható.
              </Alert>
            </>
          )}

          <TextField
            label="Megjegyzés (opcionális)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />

          {create.isError && <ProblemAlert error={create.error} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Mégse</Button>
        <Button
          variant="contained"
          disabled={!valid || create.isPending}
          onClick={() => {
            create.mutate(
              {
                barcode,
                name: name.trim(),
                locationType,
                levelCount: isShelf ? Number(levelCount) : undefined,
                notes: notes.trim() || undefined,
              },
              {
                onSuccess: (loc) => {
                  close();
                  void navigate(`/lokaciok/${loc.barcode}`);
                },
              },
            );
          }}
        >
          Létrehozás
        </Button>
      </DialogActions>
    </Dialog>
  );
}
